import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, CreditCard } from "lucide-react";
import { useFormasPagamento, useCriarFormaPagamento, useAtualizarFormaPagamento, FormaPagamento } from "@/hooks/useFormasPagamento";
import { useToast } from "@/hooks/use-toast";

export function FormasPagamento() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFormaPagamento, setEditingFormaPagamento] = useState<FormaPagamento | null>(null);
  const [formData, setFormData] = useState<{
    nome: string;
    descricao: string;
    tipo: 'pix' | 'boleto-avista' | 'boleto-parcelado' | 'cartao';
    taxa_juros: number;
    parcelas_max: number;
    taxa_maquina_cartao: number;
  }>({
    nome: "",
    descricao: "",
    tipo: "pix",
    taxa_juros: 0,
    parcelas_max: 1,
    taxa_maquina_cartao: 0,
  });

  const { data: formasPagamento, isLoading } = useFormasPagamento();
  const criarFormaPagamento = useCriarFormaPagamento();
  const atualizarFormaPagamento = useAtualizarFormaPagamento();
  const { toast } = useToast();

  const resetForm = () => {
    setFormData({
      nome: "",
      descricao: "",
      tipo: "pix",
      taxa_juros: 0,
      parcelas_max: 1,
      taxa_maquina_cartao: 0,
    });
    setEditingFormaPagamento(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingFormaPagamento) {
        await atualizarFormaPagamento.mutateAsync({
          id: editingFormaPagamento.id,
          ...formData,
        });
        toast({
          title: "Sucesso!",
          description: "Forma de pagamento atualizada com sucesso.",
        });
      } else {
        await criarFormaPagamento.mutateAsync(formData);
        toast({
          title: "Sucesso!",
          description: "Forma de pagamento criada com sucesso.",
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar forma de pagamento.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (formaPagamento: FormaPagamento) => {
    setEditingFormaPagamento(formaPagamento);
    setFormData({
      nome: formaPagamento.nome,
      descricao: formaPagamento.descricao || "",
      tipo: formaPagamento.tipo,
      taxa_juros: formaPagamento.taxa_juros || 0,
      parcelas_max: formaPagamento.parcelas_max || 1,
      taxa_maquina_cartao: Number(formaPagamento.taxa_maquina_cartao) || 0,
    });
    setIsDialogOpen(true);
  };

  const handleToggleAtivo = async (formaPagamento: FormaPagamento) => {
    try {
      await atualizarFormaPagamento.mutateAsync({
        id: formaPagamento.id,
        ativo: !formaPagamento.ativo,
      });
      toast({
        title: "Sucesso!",
        description: `Forma de pagamento ${!formaPagamento.ativo ? 'ativada' : 'desativada'} com sucesso.`,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao alterar status da forma de pagamento.",
        variant: "destructive",
      });
    }
  };

  const getTipoLabel = (tipo: string) => {
    const tipos = {
      'pix': 'PIX',
      'boleto-avista': 'Boleto à Vista',
      'boleto-parcelado': 'Boleto Parcelado',
      'cartao': 'Cartão de Crédito'
    };
    return tipos[tipo as keyof typeof tipos] || tipo;
  };

  if (isLoading) {
    return <div>Carregando...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Formas de Pagamento
            </CardTitle>
            <CardDescription>
              Gerencie as formas de pagamento disponíveis nos orçamentos
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Forma de Pagamento
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingFormaPagamento ? "Editar" : "Nova"} Forma de Pagamento
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="tipo">Tipo</Label>
                  <Select 
                    value={formData.tipo} 
                    onValueChange={(value: any) => setFormData({ ...formData, tipo: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pix">PIX</SelectItem>
                      <SelectItem value="boleto-avista">Boleto à Vista</SelectItem>
                      <SelectItem value="boleto-parcelado">Boleto Parcelado</SelectItem>
                      <SelectItem value="cartao">Cartão de Crédito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    placeholder="Como será realizado o pagamento..."
                  />
                </div>

                {(formData.tipo === 'cartao' || formData.tipo === 'boleto-parcelado') && (
                  <>
                    <div>
                      <Label htmlFor="taxa_juros">Taxa de Juros (%)</Label>
                      <Input
                        id="taxa_juros"
                        type="number"
                        step="0.01"
                        value={formData.taxa_juros}
                        onChange={(e) => setFormData({ ...formData, taxa_juros: Number(e.target.value) })}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="parcelas_max">Máximo de Parcelas</Label>
                      <Input
                        id="parcelas_max"
                        type="number"
                        min="1"
                        value={formData.parcelas_max}
                        onChange={(e) => setFormData({ ...formData, parcelas_max: Number(e.target.value) })}
                      />
                    </div>

                    {formData.tipo === 'cartao' && (
                      <div>
                        <Label htmlFor="taxa_maquina_cartao">Taxa da máquina de cartão (%)</Label>
                        <Input
                          id="taxa_maquina_cartao"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Ex: 2.72"
                          value={formData.taxa_maquina_cartao}
                          onChange={(e) => setFormData({ ...formData, taxa_maquina_cartao: Number(e.target.value) || 0 })}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Percentual descontado do total ao gerar recebimento no cartão (antes de dividir em parcelas).
                        </p>
                      </div>
                    )}
                  </>
                )}

                <Button type="submit" className="w-full">
                  {editingFormaPagamento ? "Atualizar" : "Criar"} Forma de Pagamento
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead align="center">Nome</TableHead>
                <TableHead align="center">Tipo</TableHead>
                <TableHead align="center">Taxa (%)</TableHead>
                <TableHead align="center">Taxa máq. (%)</TableHead>
                <TableHead align="center">Max. Parcelas</TableHead>
                <TableHead align="center">Status</TableHead>
                <TableHead align="center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
            {formasPagamento?.map((formaPagamento) => (
              <TableRow key={formaPagamento.id}>
                <TableCell align="left" className="font-medium">{formaPagamento.nome}</TableCell>
                <TableCell align="left">
                  <Badge variant="outline">
                    {getTipoLabel(formaPagamento.tipo)}
                  </Badge>
                </TableCell>
                <TableCell align="center">{formaPagamento.taxa_juros ?? 0}%</TableCell>
                <TableCell align="center">{formaPagamento.tipo === 'cartao' ? `${Number(formaPagamento.taxa_maquina_cartao) || 0}%` : '-'}</TableCell>
                <TableCell align="center">{formaPagamento.parcelas_max ?? 1}x</TableCell>
                <TableCell align="center">
                  <div className="flex items-center space-x-2 justify-center">
                    <Switch
                      checked={formaPagamento.ativo}
                      onCheckedChange={() => handleToggleAtivo(formaPagamento)}
                    />
                    <span className="text-sm">
                      {formaPagamento.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                </TableCell>
                <TableCell align="center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(formaPagamento)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}